import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

// ============================================================
// 首頁全底背景影片：YouTube IFrame、靜音 autoplay、無控制項
// 循環：ENDED 時 seek 回 startSeconds（不回 0）
//
// ⚠ 切換影片一律走 loadVideoById（建立一次、不 destroy 重建）。
//   原因：new YT.Player(div) 會把 React 管的 <div> 換成 iframe；若在切換時
//   destroy + 重建，React 會與被 YouTube 換掉的 DOM 打架 → reconcile 崩潰
//   （整頁白畫面）。用 loadVideoById 切換 = 純 API、零 DOM 變動，穩定。
// ============================================================

interface Props {
  youtubeId: string
  startSeconds: number
  /** true = 靜音（autoplay 需要），false = 有聲（使用者手勢後）*/
  muted?: boolean
  /** autoplay 被擋下、或 iframe 卡在非 PLAYING 狀態太久 → 父層可以露 fallback overlay 求 tap */
  onStalled?: () => void
  /** 進入 PLAYING 一次後不再認定為 stalled。父層用此清掉 fallback。 */
  onPlaying?: () => void
}

/**
 * 暴露給父層的命令式 API — onClick handler 同步呼叫 playVideo
 * iOS Low Power / Safari strict 唯一能解鎖的方式：在 user gesture call stack 裡直接 playVideo()
 */
export interface IntroBackgroundVideoHandle {
  play: () => void
}

type YTMin = {
  loadVideoById: (a: { videoId: string; startSeconds?: number }) => void
  playVideo: () => void
  pauseVideo: () => void
  mute: () => void
  unMute: () => void
  seekTo: (sec: number, allow: boolean) => void
  getCurrentTime: () => number
  destroy: () => void
}

export const IntroBackgroundVideo = forwardRef<IntroBackgroundVideoHandle, Props>(function IntroBackgroundVideo(
  { youtubeId, startSeconds, muted = true, onStalled, onPlaying },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<YTMin | null>(null)
  const [ready, setReady] = useState(false)
  // 目前 player 已載入的 `${youtubeId}@${startSeconds}`，避免重複 load
  const loadedRef = useRef<string>('')
  const stalledTimerRef = useRef<number | undefined>(undefined)
  const hasPlayedRef = useRef(false)
  // callback / 最新值放 ref，避免建立一次的 effect 閉包拿到舊值
  const onStalledRef = useRef(onStalled)
  const onPlayingRef = useRef(onPlaying)
  const youtubeIdRef = useRef(youtubeId)
  const startRef = useRef(startSeconds)
  const mutedRef = useRef(muted)
  useEffect(() => {
    onStalledRef.current = onStalled
  }, [onStalled])
  useEffect(() => {
    onPlayingRef.current = onPlaying
  }, [onPlaying])
  useEffect(() => {
    youtubeIdRef.current = youtubeId
  }, [youtubeId])
  useEffect(() => {
    startRef.current = startSeconds
  }, [startSeconds])
  useEffect(() => {
    mutedRef.current = muted
  }, [muted])

  // 建立 player — 只跑一次（切影片改走下面的 loadVideoById effect）
  useEffect(() => {
    let cancelled = false

    const ensureAPI = () =>
      new Promise<void>((resolve) => {
        if (window.YT?.Player) return resolve()
        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const tag = document.createElement('script')
          tag.src = 'https://www.youtube.com/iframe_api'
          document.head.appendChild(tag)
        }
        const prev = window.onYouTubeIframeAPIReady
        window.onYouTubeIframeAPIReady = () => {
          prev?.()
          resolve()
        }
      })

    ensureAPI().then(() => {
      if (cancelled || !containerRef.current || !window.YT) return
      const initId = youtubeIdRef.current
      const initStart = Math.floor(startRef.current)
      loadedRef.current = `${initId}@${initStart}`
      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId: initId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          // 不用 loop/playlist：切影片後 playlist 會失準；循環改由下面 ENDED → seek 手動處理
          playsinline: 1,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
          start: initStart
        },
        events: {
          onReady: (e: { target: YTMin }) => {
            setReady(true)
            if (mutedRef.current) e.target.mute()
            else e.target.unMute()
            e.target.playVideo()
            stalledTimerRef.current = window.setTimeout(() => {
              if (!hasPlayedRef.current) onStalledRef.current?.()
            }, 2500)
          },
          onStateChange: (e: { data: number }) => {
            if (e.data === 1 || e.data === 3) {
              if (!hasPlayedRef.current) {
                hasPlayedRef.current = true
                onPlayingRef.current?.()
              }
              window.clearTimeout(stalledTimerRef.current)
            }
            // ENDED = 0 — 跳回感動點手動循環（不是跳回影片 0 秒）
            if (e.data === 0) {
              playerRef.current?.seekTo(startRef.current, true)
              playerRef.current?.playVideo()
            }
          }
        }
      }) as unknown as YTMin
    })

    return () => {
      cancelled = true
      window.clearTimeout(stalledTimerRef.current)
      playerRef.current?.destroy()
      playerRef.current = null
    }
    // 只建立一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 切換影片：youtubeId / startSeconds 變 → loadVideoById（不重建 iframe）
  useEffect(() => {
    if (!ready || !playerRef.current) return
    const key = `${youtubeId}@${Math.floor(startSeconds)}`
    if (loadedRef.current === key) return
    loadedRef.current = key
    try {
      playerRef.current.loadVideoById({ videoId: youtubeId, startSeconds })
      if (mutedRef.current) playerRef.current.mute()
      else playerRef.current.unMute()
    } catch {
      /* ignore */
    }
  }, [ready, youtubeId, startSeconds])

  // muted 切換時即時 mute / unMute（使用者按音量鍵後觸發）
  useEffect(() => {
    const p = playerRef.current
    if (!p) return
    if (muted) p.mute()
    else p.unMute()
  }, [muted])

  // 暴露 imperative API — 父層的「▶ 點此開始」onClick 內直接呼叫（iOS user gesture 解鎖）
  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        try {
          playerRef.current?.playVideo()
        } catch {
          /* ignore */
        }
      }
    }),
    []
  )

  return (
    <div className="absolute inset-0 overflow-hidden bg-ink">
      {/*
        為了讓 16:9 影片撐滿各種比例的視窗：
        - width = 177.78vh（16/9 * 100vh）寬度靠高度撐開
        - height = 56.25vw（9/16 * 100vw）高度靠寬度撐開
        - min-w-full & min-h-full 確保不會小於視窗
      */}
      <div
        ref={containerRef}
        className="pointer-events-none absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh]
                   min-w-full -translate-x-1/2 -translate-y-1/2"
      />
      {/* 加強對比的漸層 overlay，讓前景文字清楚 */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/35 to-ink/85"
      />
    </div>
  )
})
